import { applySanMove } from "@chessla/shared";
import type { EvaluationScore } from "@chessla/shared";
import { buildTeachingExplanation } from "@/features/board/review/teaching-explainer";

export interface EvidenceLineStep {
  fen: string;
  san: string;
  from: string;
  to: string;
}

export const replayLine = (startingFen: string, sanMoves: readonly string[]): EvidenceLineStep[] => {
  const steps: EvidenceLineStep[] = [];
  let fen = startingFen;
  for (const san of sanMoves) {
    const move = applySanMove(fen, san);
    if (!move) break;
    steps.push({ fen: move.fen, san: move.san, from: move.uci.slice(0, 2), to: move.uci.slice(2, 4) });
    fen = move.fen;
  }
  return steps;
};

export const precedingPatternMove = (
  fen: string,
  moves: readonly { fenBefore: string; fenAfter: string; san: string }[],
): EvidenceLineStep | null => {
  const previous = moves.find((move) => move.fenAfter === fen);
  if (!previous) return null;
  const step = replayLine(previous.fenBefore, [previous.san])[0];
  return step?.fen === fen ? step : null;
};

export const recordedPatternLine = (
  example: { fenBefore?: string; san: string },
  moves: readonly { fenBefore: string; fenAfter: string; san: string }[],
): string[] => {
  const start = moves.findIndex((move) => move.fenBefore === example.fenBefore && move.san === example.san);
  if (start < 0) return [];
  const line: string[] = [];
  let fen = example.fenBefore;
  for (const move of moves.slice(start, start + 3)) {
    if (move.fenBefore !== fen || applySanMove(move.fenBefore, move.san)?.fen !== move.fenAfter) break;
    line.push(move.san);
    fen = move.fenAfter;
  }
  return line;
};

export const evaluationForPlayer = (
  moment: "before" | "after",
  before: number | null | undefined,
  after: number | null | undefined,
): number | null => (moment === "before" ? (before ?? null) : after == null ? null : -after);

export const evaluationScoreForPlayer = (
  moment: "before" | "after",
  beforeCp: number | null | undefined,
  afterCp: number | null | undefined,
  beforeMate: number | null | undefined,
  afterMate: number | null | undefined,
): EvaluationScore | null => {
  const cp = evaluationForPlayer(moment, beforeCp, afterCp);
  const rawMate = moment === "before" ? beforeMate : afterMate;
  const mate = rawMate == null ? null : moment === "before" ? rawMate : -rawMate;
  return cp == null && mate == null ? null : { cp: mate == null ? cp : null, mate };
};

const truthfulLegacyCp = (value: number | null | undefined): number | null =>
  value == null || Math.abs(value) === 10_000 ? null : value;

/** Stored PV checkpoints begin after the first continuation reply and are already normalized to the original player. */
export const lineEvaluationForPlayer = (
  step: number,
  initial: number | null | undefined,
  checkpoints: readonly number[],
): number | null => (step <= 0 ? truthfulLegacyCp(initial) : truthfulLegacyCp(checkpoints[step - 1]));

export const lineScoreForPlayer = (
  step: number,
  initial: number | null | undefined,
  scores: readonly EvaluationScore[] | null,
  legacyCheckpoints: readonly number[],
): EvaluationScore | null => {
  if (step <= 0) {
    const cp = truthfulLegacyCp(initial);
    return cp == null ? null : { cp, mate: null };
  }
  if (scores != null) return scores[step - 1] ?? null;
  const cp = truthfulLegacyCp(legacyCheckpoints[step - 1]);
  return cp == null ? null : { cp, mate: null };
};

export const evidenceEvalText = (value: number | null): string => {
  const cp = truthfulLegacyCp(value);
  if (cp == null) return "—";
  return `${cp >= 0 ? "+" : ""}${(cp / 100).toFixed(1)}`;
};

export const evidenceSwingText = (
  centipawns: number | undefined,
  before: number | null,
  after: number | null,
): string => {
  if ((before != null && truthfulLegacyCp(before) == null) || (after != null && truthfulLegacyCp(after) == null))
    return "The exact evaluation change is unavailable.";
  if (centipawns == null) return "The position became harder to play.";
  if (centipawns >= 9000) return "The move caused a decisive evaluation swing.";
  if (centipawns < 75) return "You let a useful edge slip.";
  if (centipawns < 125) return "The swing was about one pawn of value.";
  if (centipawns < 175) return "The swing was roughly a pawn and a half.";
  return `The swing was roughly ${Math.round(centipawns / 100)} pawns of value.`;
};

const advantage = (value: number | null): string => {
  if (value == null) return "The position is still unclear.";
  const owner = value >= 0 ? "You" : "Your opponent";
  const amount = Math.abs(value);
  if (amount < 20) return "The position is about even.";
  if (amount < 65) return `${owner} ${value >= 0 ? "keep" : "has"} a small edge.`;
  if (amount < 150) return `${owner} ${value >= 0 ? "now have" : "has"} a comfortable edge.`;
  return `${owner} ${value >= 0 ? "now have" : "has"} a clear advantage.`;
};

const pieceName = (san: string): string =>
  ({ N: "knight", B: "bishop", R: "rook", Q: "queen", K: "king" })[san[0]] ?? "pawn";

interface BetterPlanInput {
  fenBefore?: string;
  bestMove: string;
  reply?: string;
  payoff?: string;
  before: number | null;
  actualAfter: number | null;
  bestFinal: number | null;
  opponent: string;
  centipawnLoss?: number;
  evidence?: Record<string, unknown>;
}

export const explainBetterPlan = ({
  fenBefore,
  bestMove,
  reply,
  payoff,
  actualAfter,
  bestFinal,
  opponent,
  centipawnLoss,
  evidence,
}: BetterPlanInput): string => {
  if (fenBefore) {
    return buildTeachingExplanation({
      fenBefore,
      playedMove: "",
      bestMove,
      continuation: [reply, payoff].filter((move): move is string => Boolean(move)),
      actualAfterCp: actualAfter,
      bestFinalCp: bestFinal,
      centipawnLoss,
      opponent,
    }).explanation;
  }
  if (bestMove.endsWith("#")) return `${bestMove} delivers checkmate. The game is over.`;
  const captured = typeof evidence?.capturedRole === "string" ? evidence.capturedRole : "piece";
  const purpose = bestMove.includes("x")
    ? `${bestMove} removes the ${captured}, but the lasting benefit is the position it leaves behind.`
    : /[+#]$/.test(bestMove)
      ? `${bestMove} takes the initiative by forcing the king to respond.`
      : /^O-O/.test(bestMove)
        ? `${bestMove} improves king safety and activates the rook for the next phase.`
        : `${bestMove} improves your ${pieceName(bestMove)} while preserving the position's important resources.`;
  const sequence = reply
    ? `${opponent} can answer ${reply}${payoff ? `; then ${payoff} is the concrete payoff` : ""}.`
    : "The line keeps more useful options available on the following moves.";
  const gap = actualAfter != null && bestFinal != null ? bestFinal - actualAfter : null;
  const outcome =
    gap != null && gap >= 25
      ? `Compared with what happened, this leaves you roughly ${(gap / 100).toFixed(1)} pawns better. ${advantage(bestFinal)}`
      : `After the sequence, ${advantage(bestFinal)}`;
  return `${purpose} ${sequence} ${outcome}`;
};

export const explainLineStep = (
  san: string,
  step: number,
  evaluation: number | null,
  previousEvaluation: number | null,
  opponent: string,
): string => {
  if (san.endsWith("#")) return `${san} delivers checkmate. The game is over.`;
  if (step % 2 === 1) {
    return `The verified line continues ${san} for ${opponent}. ${advantage(evaluation)}`;
  }
  const state = advantage(evaluation);
  if (san.includes("x") && /[+#]$/.test(san))
    return `${san} takes a piece with check, so ${opponent} must answer immediately. ${state}`;
  if (san.includes("x"))
    return `${san} changes the material balance now; the important part is the position it creates next. ${state}`;
  if (/[+#]$/.test(san)) return `${san} checks the king, forcing a reply. ${state}`;
  if (/^O-O/.test(san)) return `${san} gets your king safer and brings your rook into play. ${state}`;
  if (step === 0)
    return pieceName(san) === "pawn"
      ? `${san} claims space and opens lines for your pieces. ${state}`
      : `${san} brings your ${pieceName(san)} into the game. ${state}`;
  const improved = previousEvaluation != null && evaluation != null && evaluation > previousEvaluation + 20;
  return `${san} continues the plan${improved ? " and makes your advantage clearer" : " without giving the edge back"}. ${state}`;
};
